import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Container, Row, Col, Card, Table } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { FiPhoneCall } from "react-icons/fi";
import { TfiEmail } from "react-icons/tfi";
import { FlaskConical, Beaker, Hash, RefreshCw, Droplets, Plus } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { Breadcrumbs } from "../../AbstractElements";
import { useLoader } from "../../_helper/LoaderContext";
import { useLab } from "../../_helper/PharmacyProvider";
import {
    formatDoctorDisplayDate,
    formatDoctorDisplayDateTime,
    joinStoredDateTime,
    splitStoredDateTime,
} from "../../utils/dateTime/dateFormat";
import { masterEquipment } from "../ManageEquipment/service/equipment-service";
import {
    SAMPLE_COLLECTION_STATUSES,
    SAMPLE_COLLECTION_UPDATE_STATUSES,
    canCollectStatus,
    canRecollectStatus,
    collectSample,
    getPatientSampleCollections,
    updateSampleCollection,
    updateSampleCollectionStatus,
} from "./service/sample-collection-service";
import { getTestResult } from "../TestReport/service/report-test-service";
import CollectSamplesModal from "../TestReport/CollectSamplesModal";
import S3ImageLoader from "../../S3ImageLoader";
import errorProfile from "../../assets/images/blank_profile.png";

const THEME = "#1b255b";

const stickyHeaderCell = {
    position: "sticky",
    top: 0,
    zIndex: 5,
};

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: "34px",
        borderRadius: "6px",
        borderColor: state.isFocused ? THEME : "#e5e7eb",
        boxShadow: state.isFocused ? `0 0 0 3px rgba(27, 37, 91, 0.12)` : "none",
        "&:hover": { borderColor: THEME },
        minWidth: "190px",
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: "0 6px",
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: THEME,
        borderRadius: "4px",
        margin: "1px 2px",
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: "#fff",
        fontWeight: 500,
        fontSize: "11px",
        padding: "1px 4px",
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: "#fecaca",
        borderRadius: "0 4px 4px 0",
        ":hover": {
            backgroundColor: "#b91c1c",
            color: "#fff",
        },
    }),
    placeholder: (styles) => ({
        ...styles,
        color: "#9ca3af",
        fontSize: "12px",
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        padding: "0 2px",
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: "2px",
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: "2px",
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

const statusTone = (status) => {
    const value = (status || "").toLowerCase();
    if (value === "collected" || value === "completed") {
        return { bg: "#198754", color: "#ffffff", border: "#146c43", label: status || "Pending" };
    }
    if (value === "rejected" || value === "cancelled" || value === "Re-Collected") {
        return { bg: "#dc3545", color: "#ffffff", border: "#b02a37", label: status || "Pending" };
    }
    if (value === "pending") {
        return { bg: "#ffc107", color: "#212529", border: "#e0a800", label: status || "Pending" };
    }
    return { bg: "#6c757d", color: "#ffffff", border: "#5c636a", label: status || "Pending" };
};

const toEquipmentOptions = (list = []) =>
    (Array.isArray(list) ? list : [])
        .map((item) => {
            if (!item) return null;
            if (typeof item === "string") {
                return { value: item, label: item };
            }
            const eqId = item._id || item.value;
            if (!eqId) return null;
            return {
                value: eqId,
                label: item.code ? `${item.name} (${item.code})` : item.name || eqId,
            };
        })
        .filter(Boolean);

/** Convert API ISO collectionTime → datetime-local value (YYYY-MM-DDTHH:mm) */
const toCollectionTimeInputValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const raw = String(value).trim();

    // Already datetime-local
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) {
        return raw;
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");
    // Show UTC wall clock so "2026-09-08T10:30:00.000Z" → 2026-09-08T10:30
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

/** Convert datetime-local value → ISO UTC for API */
const toCollectionTimeIso = (value) => {
    if (!value) return new Date().toISOString();

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
        return new Date(`${value}:00.000Z`).toISOString();
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return new Date().toISOString();
    return d.toISOString();
};

const pushUniqueSample = (list, sample) => {
    const id = sample?.sampleId || sample?._id;
    if (!id || list.some((item) => (item.sampleId || item._id) === id)) return;
    list.push({
        sampleId: id,
        name: sample.name || sample.sampleName || sample.sampleTypeName || "Sample",
        description: sample.description || sample.sampleDescription || "",
        assignedEquipment: sample.assignedEquipment || [],
    });
};

const samplesForTest = (test, pageTest, relatedSamples) => {
    const samples = [];
    (test?.samples || []).forEach((sample) => pushUniqueSample(samples, sample));
    (pageTest?.samples || []).forEach((sample) => pushUniqueSample(samples, sample));
    if (test?.sampleTypeId || test?.sampleTypeName) {
        pushUniqueSample(samples, {
            sampleId: test.sampleTypeId,
            name: test.sampleTypeName,
            assignedEquipment: test.assignedEquipment,
        });
    }
    if (pageTest?.sampleTypeId || pageTest?.sampleId || pageTest?.sampleTypeName || pageTest?.sampleName) {
        pushUniqueSample(samples, {
            sampleId: pageTest.sampleTypeId || pageTest.sampleId,
            name: pageTest.sampleTypeName || pageTest.sampleName,
            description: pageTest.sampleDescription,
            assignedEquipment: pageTest.assignedEquipment,
        });
    }
    (relatedSamples || []).forEach((sample) => pushUniqueSample(samples, sample));
    return samples;
};

const buildCollectOrder = ({ testOrderId, view, pageTests, pageSamples, patientName }) => {
    const orderSamples = (pageSamples || []).filter(
        (sample) => !testOrderId || !sample.testOrderId || sample.testOrderId === testOrderId
    );
    const viewTests = view?.allTests || view?.orderedTests || [];
    const sourceTests = viewTests.length ? viewTests : pageTests || [];
    const testsById = new Map();

    sourceTests.forEach((test) => {
        const id = test.testId || test._id;
        if (!id || testsById.has(id)) return;
        testsById.set(id, test);
    });

    if (!testsById.size) {
        orderSamples.forEach((sample) => {
            if (!sample.testId || testsById.has(sample.testId)) return;
            testsById.set(sample.testId, {
                testId: sample.testId,
                name: sample.testName || "Test",
            });
        });
    }

    const pageTestsById = new Map(
        (pageTests || [])
            .map((test) => [test.testId || test._id, test])
            .filter(([id]) => id)
    );

    const allTests = Array.from(testsById.entries()).map(([testId, test]) => {
        const pageTest = pageTestsById.get(testId);
        const related = orderSamples.filter((sample) => sample.testId === testId);
        return {
            testId,
            name: test.name || test.testName || pageTest?.testName || pageTest?.name || related[0]?.testName || "Test",
            samples: samplesForTest(test, pageTest, related),
            assignedEquipment: test.assignedEquipment || pageTest?.assignedEquipment || [],
        };
    });

    return {
        _id: view?._id || testOrderId,
        orderBarcodeId:
            view?.orderBarcodeId ||
            view?.barcodeId ||
            orderSamples.find((sample) => sample.orderBarcodeId)?.orderBarcodeId ||
            "",
        patient: {
            name: view?.patient?.name || view?.patientName || patientName || "—",
        },
        allTests,
    };
};

export default function OrderSampleCollection() {
    const { t } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const { setLoading } = useLoader();
    const { currentLab } = useLab();
    const [orderData, setOrderData] = useState(null);
    const [samples, setSamples] = useState([]);
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [rowEquipment, setRowEquipment] = useState({});
    const [collectionTimes, setCollectionTimes] = useState({});
    const [savingRowId, setSavingRowId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [showCollectSamples, setShowCollectSamples] = useState(false);
    const [collectOrder, setCollectOrder] = useState(null);
    const [recollectTarget, setRecollectTarget] = useState(null);

    // Route param / state = patientId (from Collected Sample list View)
    const patientId = location.state?.patientId || id;

    const patient = orderData?.patient || {};
    const orderedTests = orderData?.orderedTests || [];

    const groupedTests = useMemo(() => {
        if (orderedTests.length > 0) {
            return orderedTests.map((test) => ({
                key: test.testId,
                testId: test.testId,
                testName: test.testName || t("Untitled Test"),
                samples: samples.filter((s) => s.testId === test.testId),
            }));
        }

        const map = new Map();
        samples.forEach((sample) => {
            const key = sample.testId || sample.testName || sample._id;
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    testId: sample.testId,
                    testName: sample.testName || t("Untitled Test"),
                    samples: [],
                });
            }
            map.get(key).samples.push(sample);
        });
        return Array.from(map.values());
    }, [samples, orderedTests, t]);

    const syncRowEquipment = (sampleList, options) => {
        const next = {};
        (sampleList || []).forEach((sample) => {
            const selected = toEquipmentOptions(sample.assignedEquipment || []);
            next[sample._id] = selected.map((sel) => {
                const match = options.find((opt) => opt.value === sel.value);
                return match || sel;
            });
        });
        setRowEquipment(next);
    };

    const applyCollectionTimes = (list) => {
        const next = {};
        (list || []).forEach((sample) => {
            next[sample._id] = toCollectionTimeInputValue(sample.collectionTime);
        });
        setCollectionTimes(next);
    };

    const buildOrderFromApiData = (data = {}) => {
        const sampleList = data.samples || [];
        return {
            testOrderId: data.testOrderId || sampleList[0]?.testOrderId || "",
            status: data.orderStatus || "—",
            orderDate: data.orderDate || null,
            totalTests: data.totalTests ?? (data.orderedTests || []).length,
            sampleEntries: data.sampleEntries ?? sampleList.length,
            orderedTests: data.orderedTests || [],
            patient: {
                name: data.patientName || "—",
                age: data.patientAge,
                gender: data.patientGender,
                contact: data.mobileNumber,
                email: data.patientEmail,
                patientId: data.patientId || patientId || "",
            },
            doctor: {
                name: data.doctorName || "",
            },
        };
    };

    const fetchSamples = async (options = equipmentOptions) => {
        if (!patientId) {
            toast.warning(t("Patient ID is required"));
            return [];
        }

        const payload = {
            page: 1,
            limit: 100,
        };
        if (statusFilter) {
            payload.status = statusFilter;
        }

        const res = await getPatientSampleCollections(patientId, payload);
        if (res.data.success !== true) {
            toast.warning(res.data.message || t("Failed to load patient samples"));
            setSamples([]);
            setOrderData(buildOrderFromApiData({}));
            return [];
        }

        const data = res.data.data || {};
        const list = data.samples || [];
        setSamples(list);
        syncRowEquipment(list, options);
        applyCollectionTimes(list);
        setOrderData(buildOrderFromApiData(data));
        return list;
    };

    const fetchPageData = async () => {
        if (!patientId) {
            toast.warning(t("Patient ID is required"));
            return;
        }

        setLoading(true);
        try {
            const equipmentRes = await masterEquipment().catch(() => null);

            let options = equipmentOptions;
            if (equipmentRes?.data?.success) {
                options = toEquipmentOptions(equipmentRes.data.data || []);
                setEquipmentOptions(options);
            }

            await fetchSamples(options);
        } catch (error) {
            console.log(error);
            // toast.warning(
            //     error?.response?.data?.message || "Failed to load sample collection data"
            // );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (patientId) {
            fetchPageData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, statusFilter]);

    useEffect(() => {
        if (!equipmentOptions.length || !samples.length) return;
        syncRowEquipment(samples, equipmentOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [equipmentOptions]);

    const handleEquipmentChange = async (sample, selected) => {
        const nextSelected = selected || [];
        setRowEquipment((prev) => ({ ...prev, [sample._id]: nextSelected }));

        const previousIds = (sample.assignedEquipment || [])
            .map((eq) => (typeof eq === "string" ? eq : eq?._id))
            .filter(Boolean)
            .sort()
            .join(",");
        const nextIds = nextSelected
            .map((eq) => eq.value)
            .filter(Boolean)
            .sort()
            .join(",");

        if (previousIds === nextIds) return;

        setSavingRowId(sample._id);
        setLoading(true);
        try {
            const res = await updateSampleCollection(sample._id, {
                assignedEquipment: nextSelected.map((item) => item.value),
            });
            if (res.data.success === true) {
                toast.success(res.data.message || t("Equipment updated successfully"));
                await fetchSamples();
            } else {
                toast.warning(res.data.message || t("Unable to update equipment"));
                syncRowEquipment(samples, equipmentOptions);
            }
        } catch (error) {
            console.log(error);
            // toast.warning(error?.response?.data?.message || "Unable to update equipment");
            syncRowEquipment(samples, equipmentOptions);
        } finally {
            setSavingRowId(null);
            setLoading(false);
        }
    };

    const handleCollectionPartChange = (sampleId, part, nextValue) => {
        setCollectionTimes((prev) => {
            const current = splitStoredDateTime(prev[sampleId]);
            const nextDate = part === "date" ? nextValue || "" : current.date;
            const nextTime = part === "time" ? nextValue || "" : current.time;
            return {
                ...prev,
                [sampleId]: joinStoredDateTime(nextDate, nextTime),
            };
        });
    };

    const resolveCollectionTimePayload = (value) => {
        return toCollectionTimeIso(value);
    };

    const handleStatusChange = (sample, newStatus) => {
        if (sample.status === "Re-Collected") return;
        if (!newStatus || newStatus === sample.status) return;

        const needsReason = newStatus === "Rejected" || newStatus === "Cancelled";

        const swalConfig = needsReason
            ? {
                title: `${t(newStatus)} ${t("sample?")}`,
                html: `<p style="margin-bottom:8px;">${t("Update status from")} "<strong>${sample.status ? t(sample.status) : "-"}</strong>" ${t("to")} "<strong>${t(newStatus)}</strong>"?</p>`,
                input: "textarea",
                inputLabel: t("Reason"),
                inputPlaceholder: `${t("Enter reason for")} ${t(newStatus).toLowerCase()}...`,
                inputValidator: (value) => {
                    if (!value?.trim()) return t("Reason is required");
                    return null;
                },
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc3545",
                cancelButtonColor: "#6c757d",
                confirmButtonText: `${t("Yes")}, ${t(newStatus).toLowerCase()}`,
                cancelButtonText: t("No"),
            }
            : {
                title: t("Change status?"),
                text: `${t("Update status from")} "${sample.status ? t(sample.status) : "-"}" ${t("to")} "${t(newStatus)}"?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t("Yes, update"),
                cancelButtonText: t("No"),
            };

        Swal.fire(swalConfig).then(async (result) => {
            if (!result.isConfirmed) return;

            setLoading(true);
            try {
                const payload = { status: newStatus };
                if (newStatus === "Collected") {
                    payload.collectionTime = resolveCollectionTimePayload(collectionTimes[sample._id]);
                }
                if (needsReason && result.value?.trim()) {
                    payload.notes = result.value.trim();
                }

                const res = await updateSampleCollectionStatus(sample._id, payload);
                if (res.data.success === true) {
                    toast.success(res.data.message || t("Status updated successfully"));
                    await fetchSamples();
                } else {
                    toast.warning(res.data.message || t("Unable to update status"));
                }
            } catch (error) {
                console.log(error);
                // toast.warning(error?.response?.data?.message || "Unable to update status");
            } finally {
                setLoading(false);
            }
        });
    };

    const resolveTestOrderId = () =>
        location.state?.testOrderId ||
        orderData?.testOrderId ||
        samples.find((sample) => sample.testOrderId)?.testOrderId ||
        "";

    const openAddSample = async () => {
        const testOrderId = resolveTestOrderId();
        if (!testOrderId) {
            toast.warning(t("Test order not found"));
            return;
        }

        setLoading(true);
        let view = null;
        try {
            const res = await getTestResult(testOrderId);
            if (res.data?.success) {
                view = res.data.data || null;
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }

        setRecollectTarget(null);
        setCollectOrder(
            buildCollectOrder({
                testOrderId,
                view,
                pageTests: orderData?.orderedTests || [],
                pageSamples: samples,
                patientName: patient.name,
            })
        );
        setShowCollectSamples(true);
    };

    const handleCollect = (sample) => {
        if (!canCollectStatus(sample.status)) {
            toast.warning(t("Only Pending samples can be collected."));
            return;
        }

        Swal.fire({
            title: t("Collect sample?"),
            text: `${t("Mark barcode")} ${sample.barcodeId || ""} ${t("as Collected?")}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#198754",
            cancelButtonColor: "#6c757d",
            confirmButtonText: t("Yes, collect"),
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            setLoading(true);
            try {
                const res = await collectSample(sample._id, {
                    collectionTime: resolveCollectionTimePayload(collectionTimes[sample._id]),
                });
                if (res.data.success === true) {
                    toast.success(res.data.message || t("Sample collected successfully."));
                    await fetchSamples();
                } else {
                    toast.warning(res.data.message || t("Unable to collect sample"));
                }
            } catch (error) {
                console.log(error);
                // toast.warning(error?.response?.data?.message || "Unable to collect sample");
            } finally {
                setLoading(false);
            }
        });
    };

    const handleRecollect = async (sample) => {
        if (!canRecollectStatus(sample.status)) {
            toast.warning(t("Only Rejected or Cancelled samples can be recollected."));
            return;
        }

        const testOrderId = sample.testOrderId || resolveTestOrderId();
        if (!testOrderId) {
            toast.warning(t("Test order not found"));
            return;
        }

        setLoading(true);
        let view = null;
        try {
            const res = await getTestResult(testOrderId);
            if (res.data?.success) {
                view = res.data.data || null;
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }

        const order = buildCollectOrder({
            testOrderId,
            view,
            pageTests: orderData?.orderedTests || [],
            pageSamples: samples,
            patientName: patient.name,
        });
        if (sample.testId && !order.allTests.some((test) => test.testId === sample.testId)) {
            order.allTests.push({
                testId: sample.testId,
                name: sample.testName || "Test",
                sampleTypeId: sample.sampleId,
                sampleTypeName: sample.sampleName,
                samples: sample.sampleId
                    ? [{
                        sampleId: sample.sampleId,
                        name: sample.sampleName || "Sample",
                        description: sample.sampleDescription || "",
                        assignedEquipment: sample.assignedEquipment || [],
                    }]
                    : [],
            });
        }

        setCollectOrder(order);
        setRecollectTarget(sample);
        setShowCollectSamples(true);
    };

    if (!orderData) {
        return (
            <>
                <Breadcrumbs
                    mainTitle={t("Sample Collection")}
                    parent={t("Home")}
                    subParents={t("Collected Sample")}
                    title={t("Sample Collection")}
                />
                <Container fluid>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t("Loading...")}</span>
                        </div>
                    </div>
                </Container>
            </>
        );
    }

    return (
        <>
            <Breadcrumbs
                mainTitle={t("Sample Collection")}
                parent={t("Home")}
                subParents={t("Collected Sample")}
                title={t("Sample Collection")}
            />
            <Container fluid={true}>
                <Row>
                    <Col>
                        <div>

                            {/* Patient hero */}
                            <Card
                                className="mb-3 border-0 overflow-hidden"
                                style={{
                                    borderRadius: "16px",
                                    boxShadow: "0 10px 30px rgba(27, 37, 91, 0.14)",
                                    background: `linear-gradient(120deg, ${THEME} 0%, #24316d 45%, #3a4c9e 100%)`,
                                }}
                            >
                                <div className="text-white" style={{ padding: "12px 16px" }}>
                                    <div
                                        className="d-flex align-items-center flex-wrap"
                                        style={{ gap: "14px", justifyContent: "space-between" }}
                                    >
                                        <div className="d-flex align-items-center" style={{ gap: "14px", minWidth: 0 }}>
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{
                                                    width: 62,
                                                    height: 62,
                                                    padding: 2,
                                                    background: "rgba(255,255,255,0.22)",
                                                    border: "1px solid rgba(255,255,255,0.28)",
                                                }}
                                            >
                                                <S3ImageLoader
                                                    fileName={patient?.profile_picture}
                                                    fallbackSrc={errorProfile}
                                                    className="rounded-circle"
                                                    alt={t("Patient")}
                                                    style={{
                                                        width: 56,
                                                        height: 56,
                                                        objectFit: "cover",
                                                        background: "#fff",
                                                    }}
                                                />
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <h4
                                                    className="mb-1 fw-bold text-white"
                                                    style={{ fontSize: "1.25rem", letterSpacing: "0.2px" }}
                                                >
                                                    {patient.name || "—"}
                                                </h4>
                                                <div
                                                    className="text-capitalize"
                                                    style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.88rem" }}
                                                >
                                                    {patient.gender || "—"}
                                                    {patient.age !== undefined && patient.age !== null && patient.age !== ""
                                                        ? ` · ${t("Age")} ${patient.age}`
                                                        : ""}
                                                </div>
                                                {orderData.doctor?.name ? (
                                                    <div
                                                        className="mt-0.5"
                                                        style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}
                                                    >
                                                        {orderData.doctor.name}
                                                    </div>
                                                ) : null}
                                                {orderData.orderDate ? (
                                                    <div
                                                        className="mt-0.5"
                                                        style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}
                                                    >
                                                        {t("Order Date")}:{" "}
                                                        {formatDoctorDisplayDateTime(orderData.orderDate, currentLab)}
                                                    </div>
                                                ) : null}
                                                {patient.contact ? (
                                                    <div className="d-flex align-items-center gap-2 mt-0.5">
                                                        <FiPhoneCall size={14} />
                                                        <span style={{ fontSize: "0.85rem" }}>{patient.contact}</span>
                                                    </div>
                                                ) : null}
                                                {patient.email ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <TfiEmail size={14} />
                                                        <span style={{ fontSize: "0.85rem" }}>{patient.email}</span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div
                                            className="d-flex flex-wrap"
                                            style={{ gap: "10px" }}
                                        >
                                            {[
                                                {
                                                    label: t("Tests"),
                                                    value: orderData.totalTests ?? orderedTests.length ?? 0,
                                                },
                                                {
                                                    label: t("Sample entries"),
                                                    value: orderData.sampleEntries ?? samples.length,
                                                },
                                                {
                                                    label: t("Order Status"),
                                                    value: orderData.status || "—",
                                                    capitalize: true,
                                                },
                                            ].map((item) => (
                                                <div
                                                    key={item.label}
                                                    style={{
                                                        background: "rgba(255,255,255,0.12)",
                                                        borderRadius: "10px",
                                                        padding: "8px 14px",
                                                        minWidth: "105px",
                                                        backdropFilter: "blur(6px)",
                                                    }}
                                                >
                                                    <span
                                                        className="d-block"
                                                        style={{
                                                            color: "rgba(255,255,255,0.65)",
                                                            fontSize: "0.74rem",
                                                            marginBottom: "2px",
                                                        }}
                                                    >
                                                        {item.label}
                                                    </span>
                                                    <strong
                                                        className={item.capitalize ? "text-capitalize" : ""}
                                                        style={{ fontSize: "0.95rem" }}
                                                    >
                                                        {item.value}
                                                    </strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            {samples.length === 0 ? (
                                <Card
                                    className="border-0 text-center py-5"
                                    style={{
                                        borderRadius: "18px",
                                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
                                    }}
                                >
                                    <div className="d-flex flex-column align-items-center gap-3 text-muted px-3">
                                        <Beaker size={36} style={{ color: THEME, opacity: 0.45 }} />
                                        <div className="fw-semibold">{t("No sample collection entries found")}</div>
                                        <small>{t("Samples for this test order will appear here once available.")}</small>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={openAddSample}
                                        >
                                            <Plus size={14} />
                                            {t("Add Sample")}
                                        </button>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ maxWidth: "200px" }}
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="">{t("All Status")}</option>
                                            {SAMPLE_COLLECTION_STATUSES.map((option) => (
                                                <option key={option} value={option}>
                                                    {t(option)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </Card>
                            ) : (
                                <Card
                                    className="border-0 overflow-hidden"
                                    style={{
                                        borderRadius: "18px",
                                        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.07)",
                                    }}
                                >
                                    <div
                                        className="px-4 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
                                    // style={{
                                    //     background: "linear-gradient(90deg, #f4f6fb 0%, #ffffff 100%)",
                                    //     borderBottom: "1px solid #eef1f6",
                                    // }}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                                onClick={openAddSample}
                                            >
                                                <Plus size={14} />
                                                {t("Add Sample")}
                                            </button>
                                            {/* <div
                                                className="d-flex align-items-center justify-content-center rounded-3"
                                                style={{
                                                    width: 44,
                                                    height: 44,
                                                    background: THEME,
                                                    color: "#fff",
                                                }}
                                            >
                                                <FlaskConical size={20} />
                                            </div> */}
                                            {/* <div>
                                                <h5 className="mb-0 fw-bold" style={{ color: THEME }}>
                                                    {groupedTests.length > 0
                                                        ? groupedTests.map((g) => g.testName).join(", ")
                                                        : samples[0]?.testName || "Test"}
                                                </h5>
                                                {groupedTests.length === 1 && samples[0]?.sampleName ? (
                                                    <small className="text-muted">
                                                        Sample type: {samples[0].sampleName}
                                                    </small>
                                                ) : groupedTests.length > 1 ? (
                                                    <small className="text-muted">
                                                        {groupedTests.length} tests ordered
                                                    </small>
                                                ) : null}
                                            </div> */}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ minWidth: "140px" }}
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                            >
                                                <option value="">{t("All Status")}</option>
                                                {SAMPLE_COLLECTION_STATUSES.map((option) => (
                                                    <option key={option} value={option}>
                                                        {t(option)}
                                                    </option>
                                                ))}
                                            </select>
                                            <span
                                                className="badge rounded-pill px-3 py-2"
                                                style={{
                                                    background: "rgba(27, 37, 91, 0.08)",
                                                    color: THEME,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {samples.length} {samples.length > 1 ? t("samples") : t("sample")}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="p-3 pt-0">
                                        <div
                                            className="tw-rounded-lg tw-border tw-border-[#e5e7eb] custom-slim-scrollbar"
                                            style={{
                                                overflowX: "auto",
                                                overflowY: "auto",
                                                maxHeight: "520px",
                                            }}
                                        >
                                            <Table
                                                className="mb-0 table-bordered table-hover align-middle doctor-table table-center"
                                            // style={{
                                            //     tableLayout: "fixed",
                                            //     minWidth: "1100px",
                                            //     width: "100%",
                                            // }}
                                            >
                                                <thead>
                                                    <tr>
                                                        <th
                                                            className="bg-primary text-white text-center"
                                                            style={stickyHeaderCell}
                                                        >
                                                            #
                                                        </th>
                                                        <th
                                                            className="bg-primary text-white"
                                                            style={stickyHeaderCell}
                                                        >
                                                            {t("Order Barcode")}
                                                        </th>
                                                        <th
                                                            className="bg-primary text-white"
                                                            style={{ ...stickyHeaderCell, width: "180px", minWidth: "180px" }}
                                                        >
                                                            {t("Test Name")}
                                                        </th>
                                                        <th
                                                            className="bg-primary text-white"
                                                            style={stickyHeaderCell}
                                                        >
                                                            {t("Sample Type")}
                                                        </th>
                                                        <th
                                                            className="bg-primary text-white"
                                                            style={{ ...stickyHeaderCell, width: "170px", minWidth: "170px" }}
                                                        >
                                                            {t("Collection Date & Time")}
                                                        </th>
                                                        <th
                                                            className="bg-primary text-white"
                                                            style={{ ...stickyHeaderCell, width: "190px", minWidth: "190px" }}
                                                        >
                                                            {t("Assigned Equipment")}
                                                        </th>
                                                        <th
                                                            className="bg-primary text-white"
                                                            style={stickyHeaderCell}
                                                        >
                                                            {t("Status")}
                                                        </th>
                                                        <th
                                                            className="bg-primary text-white text-center"
                                                            style={stickyHeaderCell}
                                                        >
                                                            {t("Action")}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {samples.map((sample, index) => {
                                                        const tone = statusTone(sample.status);
                                                        const isPending =
                                                            String(sample.status || "Pending").toLowerCase() ===
                                                            "pending";
                                                        const canCollect = canCollectStatus(sample.status);
                                                        const canRecollect = canRecollectStatus(sample.status);
                                                        const collectionTimeValue =
                                                            collectionTimes[sample._id] ??
                                                            toCollectionTimeInputValue(sample.collectionTime);
                                                        const formattedCollectionDateTime =
                                                            formatDoctorDisplayDateTime(
                                                                sample.collectionTime || collectionTimes[sample._id],
                                                                currentLab
                                                            ) ||
                                                            formatDoctorDisplayDate(
                                                                sample.collectionTime || collectionTimes[sample._id],
                                                                currentLab
                                                            ) ||
                                                            "—";
                                                        return (
                                                            <tr key={sample._id}>
                                                                <td className="text-center fw-bold">{index + 1}</td>
                                                                <td>
                                                                    {sample.orderBarcodeId ? (
                                                                        <div className="fw-semibold text-nowrap" style={{ color: THEME }}>
                                                                            {sample.orderBarcodeId}
                                                                        </div>
                                                                    ) : null}
                                                                </td>
                                                                <td>
                                                                    <div className="fw-semibold" style={{ color: THEME }}>
                                                                        {sample.testName || "—"}
                                                                        {sample.notes?.trim() ? (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    data-tooltip-id={`sample-notes-${sample._id}`}
                                                                                    aria-label={t("View notes")}
                                                                                    onClick={() =>
                                                                                        Swal.fire({
                                                                                            title: t("Notes"),
                                                                                            text: sample.notes,
                                                                                            icon: "info",
                                                                                            confirmButtonColor: THEME,
                                                                                            confirmButtonText: t("Close"),
                                                                                        })
                                                                                    }
                                                                                    style={{
                                                                                        border: "none",
                                                                                        background: "transparent",
                                                                                        padding: 0,
                                                                                        marginLeft: "6px",
                                                                                        lineHeight: 1,
                                                                                        color: "#6b7280",
                                                                                        verticalAlign: "text-top",
                                                                                        cursor: "pointer",
                                                                                    }}
                                                                                >
                                                                                    <i className="bi bi-info-circle" style={{ fontSize: "14px" }}></i>
                                                                                </button>
                                                                                <Tooltip
                                                                                    id={`sample-notes-${sample._id}`}
                                                                                    place="top"
                                                                                    content={sample.notes}
                                                                                    style={{ maxWidth: "280px", whiteSpace: "normal", zIndex: 1080 }}
                                                                                />
                                                                            </>
                                                                        ) : null}
                                                                    </div>
                                                                    {sample.barcodeId ? (
                                                                        <div
                                                                            className="d-inline-flex align-items-center gap-1 mt-1"
                                                                            style={{
                                                                                color: "#6b7280",
                                                                                fontSize: "12px",
                                                                            }}
                                                                        >
                                                                            <Hash size={12} />
                                                                            {sample.barcodeId}
                                                                        </div>
                                                                    ) : null}
                                                                </td>
                                                                <td>
                                                                    {sample.sampleName || "—"}
                                                                    {/* {sample.barcodeId ? (
                                                                        <div
                                                                            className="d-inline-flex align-items-center gap-1 mt-1"
                                                                            style={{
                                                                                color: "#6b7280",
                                                                                fontSize: "12px",
                                                                            }}
                                                                        >
                                                                            <Hash size={12} />
                                                                            {sample.barcodeId}
                                                                        </div>
                                                                    ) : null} */}
                                                                </td>
                                                                <td>
                                                                    <span className="text-nowrap">
                                                                        {formattedCollectionDateTime}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <Select
                                                                        isMulti
                                                                        options={equipmentOptions}
                                                                        value={rowEquipment[sample._id] || []}
                                                                        onChange={(selected) =>
                                                                            handleEquipmentChange(sample, selected)
                                                                        }
                                                                        styles={selectStyles}
                                                                        placeholder={t("Select equipment...")}
                                                                        isDisabled={
                                                                            !isPending || savingRowId === sample._id
                                                                        }
                                                                        classNamePrefix="react-select"
                                                                        menuPortalTarget={document.body}
                                                                        menuPosition="fixed"
                                                                        menuPlacement="auto"
                                                                    />
                                                                    {savingRowId === sample._id ? (
                                                                        <small className="text-muted">{t("Saving...")}</small>
                                                                    ) : null}
                                                                </td>
                                                                <td>
                                                                    <select
                                                                        value={sample.status || "Pending"}
                                                                        disabled={sample.status === "Re-Collected"}
                                                                        onChange={(e) =>
                                                                            handleStatusChange(sample, e.target.value)
                                                                        }
                                                                        style={{
                                                                            width: "100%",
                                                                            minWidth: "100px",
                                                                            padding: "4px 8px",
                                                                            height: "32px",
                                                                            borderRadius: "6px",
                                                                            fontSize: "12px",
                                                                            fontWeight: 700,
                                                                            cursor: sample.status === "Re-Collected" ? "not-allowed" : "pointer",
                                                                            outline: "none",
                                                                            border: `1px solid ${tone.border}`,
                                                                            background: tone.bg,
                                                                            color: tone.color,
                                                                        }}
                                                                    >
                                                                        {!SAMPLE_COLLECTION_UPDATE_STATUSES.includes(sample.status) &&
                                                                            sample.status ? (
                                                                            <option value={sample.status}>
                                                                                {t(sample.status)}
                                                                            </option>
                                                                        ) : null}
                                                                        {SAMPLE_COLLECTION_UPDATE_STATUSES.map((option) => (
                                                                            <option
                                                                                key={option}
                                                                                value={option}
                                                                                style={{
                                                                                    background: "#ffffff",
                                                                                    color: "#212529",
                                                                                }}
                                                                            >
                                                                                {t(option)}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                                <td className="text-center">
                                                                    {canCollect ? (
                                                                        <button
                                                                            type="button"
                                                                            className="success-action-button"
                                                                            title={t("Collect sample")}
                                                                            onClick={() => handleCollect(sample)}
                                                                        >
                                                                            <i className="bi bi-droplet-half" style={{ fontSize: "18px" }}></i>
                                                                            {/* <Droplets size={16} /> */}
                                                                        </button>
                                                                    ) : canRecollect ? (
                                                                        <button
                                                                            type="button"
                                                                            className="danger-action-button"
                                                                            title={t("Recollect sample")}
                                                                            onClick={() => handleRecollect(sample)}
                                                                        >
                                                                            <RefreshCw size={16} />
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-muted">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                </Card>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container>

            <CollectSamplesModal
                show={showCollectSamples}
                onHide={() => {
                    setShowCollectSamples(false);
                    setRecollectTarget(null);
                }}
                order={collectOrder}
                startEmpty={!recollectTarget}
                redirectOnSuccess={false}
                recollectSampleId={recollectTarget?._id || null}
                recollectSample={recollectTarget}
                onSuccess={() => fetchSamples()}
            />
        </>
    );
}
